import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router'

import { QUERY_KEYS } from 'constants/constants'
import {
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../context'
import { MODES } from '../features/pricing/entitlements'
import {
  applyTalentLocks,
  lockedIdsFor,
  rankTalentStorageCached
} from '../features/pricing/usage/talent-storage'
import { query } from '../graphql'
import {
  careerActiveFilterInput,
  talentContextFilterInput
} from '../graphql/filter-inputs'
import { shouldFullFetchTalents } from '../helpers/sample-data'
import { getSpacesTableData } from '../helpers/spaces-table-data'

// Key und queryFn ohne select. Die rohen Daten sind [spaces, careers], spaces
// ohne soft_deleted. Der Talentspeicher (features/pricing) rankt auf genau
// diesem Stand und teilt sich so den Cache mit der Talentliste.
export const talentsQueryOptions = ({ partnerId, username, queryClient }) => ({
  queryKey: QUERY_KEYS.talentsByPartner(partnerId, username),
  queryFn: () => {
    // Stand zur Fetch-Zeit lesen, nicht beim Render. Sonst hält die queryFn
    // den alten Stand als Closure, und ein Refetch nach resetQueries holt
    // nur ein Delta auf die alten Daten.
    const talentsQueryState = queryClient.getQueryState(
      QUERY_KEYS.talentsByPartner(partnerId, username)
    )
    const cachedSpaces =
      talentsQueryState?.status === 'success'
        ? talentsQueryState.data?.[0]
        : undefined
    // Hart gelöschte Beispiel-Spaces tauchen in keinem Delta auf. Solange
    // der Cache welche enthält, wird voll geladen (C1).
    const isDelta = !shouldFullFetchTalents(cachedSpaces)

    return Promise.all([
      new Promise((resolve, reject) => {
        query({
          query: 'spacesByPartnerIdByUpdatedAt',
          variables: {
            partner_id: partnerId,
            updatedAt: isDelta
              ? {
                  gt: new Date(talentsQueryState.dataUpdatedAt).toISOString()
                }
              : undefined,
            filter: talentContextFilterInput
          }
        })
          .then((response) => {
            if (isDelta) {
              const responseIds = new Set(response.map(({ id }) => id))

              resolve(
                // select: [0] = spaces / [1] = careers
                cachedSpaces
                  .filter(({ id }) => !responseIds.has(id))
                  .concat(response)
                  .filter(({ soft_deleted }) => !soft_deleted)
              )

              return
            }

            resolve(response.filter(({ soft_deleted }) => !soft_deleted))
          })
          .catch((err) => reject(err))
      }),
      query({
        query: 'careerByPartnerByDate',
        variables: {
          partner_id: partnerId,
          filter: careerActiveFilterInput
        }
      })
    ])
  }
})

// Sperrstand des Talentspeichers für den select. off heißt unverändert
// (legacy, preview, unbegrenzt oder ohne gültiges Limit, fail-open). pending
// heißt vorläufig, mit derselben Bedingung wie provisional in
// useTalentStorage (features/pricing/hooks.js). enforced trägt das Limit.
export const TALENT_LOCK_MODES = Object.freeze({
  OFF: 'off',
  PENDING: 'pending',
  ENFORCED: 'enforced'
})

const LOCK_OFF = Object.freeze({ mode: TALENT_LOCK_MODES.OFF, limit: null })
const LOCK_PENDING = Object.freeze({
  mode: TALENT_LOCK_MODES.PENDING,
  limit: null
})

export const talentLockOf = (entitlements) => {
  if (!entitlements) return LOCK_OFF

  const provisional =
    !entitlements.settled &&
    (!!entitlements.planPayment || entitlements.planFieldSettled === false)
  if (provisional) return LOCK_PENDING

  const base = entitlements.limits?.talentStorage
  if (entitlements.mode !== MODES.ENFORCED || !base?.enforced) return LOCK_OFF

  const { limit } = base
  if (typeof limit !== 'number' || !Number.isFinite(limit) || limit < 0) {
    return LOCK_OFF
  }

  return { mode: TALENT_LOCK_MODES.ENFORCED, limit: Math.floor(limit) }
}

const maskSpaces = (spaces, mode, limit) => {
  const index = rankTalentStorageCached(spaces)

  if (mode === TALENT_LOCK_MODES.PENDING) {
    return applyTalentLocks(
      spaces,
      new Set(index.rankById.keys()),
      index.rankById
    ).map((row) =>
      row?.storage_locked ? { ...row, storage_pending: true } : row
    )
  }

  if (mode !== TALENT_LOCK_MODES.ENFORCED) return spaces

  return applyTalentLocks(spaces, lockedIdsFor(index, limit), index.rankById)
}

// Einmal je Datenstand und Sperrstand. Der Query-Cache liefert bei gleichen
// Daten dieselbe Array-Referenz, ein neuer Stand ist ein neues Array.
const maskCache = new WeakMap()

/**
 * Maskiert die rohen Spaces der Talents-Query nach dem Talentspeicher. Gerankt
 * wird auf genau dem Array aus dem Cache, so trifft rankTalentStorageCached
 * denselben Index wie useTalentStorage. Gesperrt wird je Person, also jede
 * Zeile einer Person ab Rang N+1, auch eine, die selbst nicht zählt (etwa
 * ATTRACT ohne mail_stored). Gesperrte Zeilen tragen storage_locked,
 * storage_rank (Rang der Person) und Platzhalter statt Score, Matching und
 * Zusatzangaben. Vorläufig (pending) gilt das für jede Zeile mit Rang, dazu
 * kommt storage_pending. Bei off bleibt die Referenz gleich. Das Ergebnis ist
 * je Datenstand und Sperrstand gecacht, dieselben Eingaben liefern dieselbe
 * Referenz.
 *
 * @param {Array} spaces rohe Spaces (response[0])
 * @param {{ mode: string, limit: number|null }} lock aus talentLockOf
 */
export const lockTalentSpaces = (spaces, { mode, limit } = LOCK_OFF) => {
  if (!Array.isArray(spaces) || mode === TALENT_LOCK_MODES.OFF) return spaces

  const key = `${mode}:${limit}`
  let byLock = maskCache.get(spaces)
  if (!byLock) {
    byLock = new Map()
    maskCache.set(spaces, byLock)
  }
  if (!byLock.has(key)) byLock.set(key, maskSpaces(spaces, mode, limit))

  return byLock.get(key)
}

export const useTalentsQuery = () => {
  const { partner } = usePartnerContext()
  const {
    cognitoUser: { username }
  } = useUserContext()
  const { entitlements } = usePaymentContext() || {}

  const queryClient = useQueryClient()

  const { state: locationState } = useLocation()

  const lock = talentLockOf(entitlements)

  const talentsQuery = useQuery({
    ...talentsQueryOptions({ partnerId: partner.id, username, queryClient }),
    // Bewusst inline wie vor dem Talentspeicher. react-query rechnet den
    // select so bei jedem Render neu, und getSpacesTableData hängt an der
    // Uhrzeit (eine Einladung läuft ab, ohne dass sich die Daten ändern). Nur
    // die Maske ist je Datenstand gecacht (lockTalentSpaces).
    select: (response) => {
      try {
        const spaces = lockTalentSpaces(response[0], lock)

        // return { spaces: [], careers: [] }
        return {
          spaces: getSpacesTableData({
            spaces: spaces.filter(({ context, mail_stored }) =>
              context === 'ATTRACT' ? mail_stored : true
            ),
            user_id: username,
            customStatuses: partner.talentStatuses
          }),
          careers: response[1].filter(({ id }) => !id.includes('PRODUCT_TOUR'))
        }
      } catch (err) {
        console.error(err)
        return { spaces: [], careers: [] }
      }
    }
  })

  if (locationState?.productTourData) {
    return {
      ...talentsQuery,
      data: locationState.productTourData,
      status: 'success'
    }
  }

  return talentsQuery
}
